import { unique } from "radash";
import { useMemo } from "react";
import type { Permission } from "~/models/permission";
import type { User } from "~/models/user";
import { useOptionalAuth, useOptionalRootContext } from "../auth/hooks";

export interface PermissionArgs {
  user: User;
  allPermissions?: boolean;
  labelId?: string | null;
  artistId?: string | null;
}

function getLabelPermissions(user: User, labelId: string) {
  const label = user.labels.find((label) => label.uid === labelId);
  return label?.permissions || [];
}

function getArtistPermissions(user: User, artistId: string) {
  const artist = user.artists.find((artist) => artist.uid === artistId);
  if (!artist) return [];

  if (artist.permissions.length > 0) return artist.permissions;

  return getLabelPermissions(user, artist.label.uid);
}

function getAllUserPermissions(user: User) {
  return unique([
    ...user.labels.flatMap((label) => label.permissions),
    ...user.artists.flatMap((artist) => artist.permissions),
  ]);
}

export function getPermissions({
  allPermissions,
  artistId,
  labelId,
  user,
}: PermissionArgs) {
  if (allPermissions) return getAllUserPermissions(user);

  if (!labelId && !artistId) return [];

  if (artistId && artistId !== "all") {
    const artistPermissions = getArtistPermissions(user, artistId);
    return artistPermissions.length > 0 || !labelId
      ? artistPermissions
      : getLabelPermissions(user, labelId);
  }

  return labelId ? getLabelPermissions(user, labelId) : [];
}

export function hasPermissions(
  userPermissions: Array<string>,
  requiredPermissions: Array<Permission | RegExp>,
) {
  return requiredPermissions.every((required) =>
    typeof required === "string"
      ? userPermissions.includes(required)
      : userPermissions.some((user) => required.test(user)),
  );
}

export function hasSomePermissions(
  userPermissions: Array<string>,
  requiredPermissions: Array<Permission | RegExp>,
) {
  return requiredPermissions.some((required) =>
    typeof required === "string"
      ? userPermissions.includes(required)
      : userPermissions.some((user) => required.test(user)),
  );
}

export function usePermissions(context?: Omit<PermissionArgs, "user">) {
  const auth = useOptionalAuth();
  const appContext = useOptionalRootContext();

  const allPermissions = useMemo(() => {
    if (!auth) {
      return [];
    }

    const permissionArgs: PermissionArgs = {
      user: auth.user,
      ...(context || {
        labelId: appContext?.label?.uid,
        artistId: appContext?.artist?.uid,
      }),
    };

    return getPermissions(permissionArgs);
  }, [context?.artistId, context?.labelId, appContext, auth]);

  return allPermissions;
}

export function useHasPermissions(
  requiredPermissions: Array<Permission | RegExp>,
  context?: Pick<PermissionArgs, "labelId" | "artistId">,
) {
  const userPermissions = usePermissions(context);
  return hasPermissions(userPermissions, requiredPermissions);
}

export function useHasSomePermissions(permissions: Array<Permission | RegExp>) {
  const userPermissions = usePermissions();
  return hasSomePermissions(userPermissions, permissions);
}
